import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { TRUCKING_IMG } from "@/lib/constants";
import { Truck } from "lucide-react";

const MATERIALS = [
  "Aggregate", "Sand", "Gravel", "Topsoil",
  "Equipment Transport", "Construction Material", "General Hauling",
];

export default function TruckingPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    material_type: "Aggregate", pickup_location: "", delivery_location: "",
    quantity: "", date_required: "", notes: "",
  });
  const [s, setS] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast("Sign in to request a quote"); return nav("/login"); }
    setS(true);
    try {
      const { data } = await api.post("/bookings/trucking", form);
      toast.success(`Quote request submitted — ${data.invoice_number}`);
      nav("/dashboard");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setS(false); }
  };

  return (
    <Layout>
      <section className="relative">
        <div className="absolute inset-0"><img src={TRUCKING_IMG} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-[#1D4ED8]/85" /></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-white">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#FFCE00]">Service · Trucking</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl mt-2">Heavy hauling. Custom quotes.</h1>
          <p className="mt-3 max-w-xl text-white/90">Tell us what you need moved — we'll send a custom XCD quote.</p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white border border-slate-200 rounded-md p-6 sm:p-8">
          <div className="flex items-center gap-2 text-[#1D4ED8]"><Truck className="h-5 w-5" strokeWidth={2.5} /><span className="tag-overline">Request quote</span></div>
          <h2 className="font-heading font-black text-2xl mt-1">Trucking quote request</h2>
          <form onSubmit={submit} className="mt-6 space-y-5">
            <div>
              <Label className="text-sm font-bold text-slate-900">Material / Service</Label>
              <Select value={form.material_type} onValueChange={(v) => setForm({ ...form, material_type: v })}>
                <SelectTrigger data-testid="trucking-material"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MATERIALS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-bold text-slate-900">Pickup location</Label>
                <Input data-testid="trucking-pickup" required value={form.pickup_location} onChange={e => setForm({ ...form, pickup_location: e.target.value })} />
              </div>
              <div>
                <Label className="text-sm font-bold text-slate-900">Delivery location</Label>
                <Input data-testid="trucking-delivery" required value={form.delivery_location} onChange={e => setForm({ ...form, delivery_location: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-bold text-slate-900">Quantity</Label>
                <Input data-testid="trucking-quantity" placeholder="e.g. 5 cu yards" required value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} />
              </div>
              <div>
                <Label className="text-sm font-bold text-slate-900">Date required</Label>
                <Input data-testid="trucking-date" type="date" required value={form.date_required} onChange={e => setForm({ ...form, date_required: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-sm font-bold text-slate-900">Notes</Label>
              <Textarea data-testid="trucking-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>
            <Button type="submit" disabled={s} data-testid="trucking-submit-btn"
                    className="w-full bg-[#E32636] hover:bg-[#C91F2C] text-white font-bold h-12">
              {s ? "Submitting…" : "Submit quote request"}
            </Button>
          </form>
        </div>
      </section>
    </Layout>
  );
}
